/**
 * FinanceEntityModal — "New entity" creation modal.
 *
 * Sits on top of /api/finance/entities POST. Supports linking a Financial
 * Entity to an existing legal entity (entities_v2.entity_id) at creation
 * time so the legal and fiscal sides share a single conceptual entity —
 * see `linkedLegalEntityId` prop and the legal-link picker below.
 */
import React, { useEffect, useState } from 'react';
import { X, Save, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function FinanceEntityModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', currency: 'QAR', sector: '', notes: '', legal_entity_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [legalEntities, setLegalEntities] = useState([]);
  const [legalLoading, setLegalLoading] = useState(true);

  // Pull legal entities from entities_v2 so the user can link this fiscal
  // entity back to its legal counterpart. Falls back to "none" gracefully
  // when the caller has no /entities-os access.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/v2/entities/');
        setLegalEntities(Array.isArray(data) ? data : (data?.entities || []));
      } catch {
        setLegalEntities([]);
      } finally {
        setLegalLoading(false);
      }
    })();
  }, []);

  // When a legal entity is picked, prefill name + currency so the user
  // doesn't have to re-type a name they already entered in entities-os.
  const pickLegal = (legalId) => {
    setForm((f) => ({ ...f, legal_entity_id: legalId }));
    const legal = legalEntities.find((e) => e.entity_id === legalId);
    if (legal) {
      setForm((f) => ({
        ...f,
        legal_entity_id: legalId,
        name: f.name || legal.name || legal.legal_name || '',
        sector: f.sector || legal.sector || legal.industry || '',
      }));
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.legal_entity_id) delete payload.legal_entity_id;
      const { data } = await api.post('/finance/entities', payload);
      toast.success(`Entity "${data.name}" created`);
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create entity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="finance-create-entity-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="text-lg font-semibold text-slate-900">New entity</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {/* Legal entity link — at the top so it can auto-fill below */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <Link2 className="w-3 h-3" /> Linked legal entity (optional)
            </label>
            <select
              value={form.legal_entity_id}
              onChange={(e) => pickLegal(e.target.value)}
              data-testid="finance-create-legal-link"
              disabled={legalLoading}
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#8B3FC5]"
            >
              <option value="">— No legal record (fiscal-only) —</option>
              {legalEntities.map((e) => (
                <option key={e.entity_id} value={e.entity_id}>
                  {(e.legal_name || e.name || e.entity_id)} {e.cr_number ? `· CR ${e.cr_number}` : ''}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-slate-400 mt-1">
              Linking surfaces the entity&rsquo;s CR, baladiya & shareholders next to its cash position.
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="ToothRocket Labs"
              data-testid="finance-create-name"
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#8B3FC5]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Currency</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                data-testid="finance-create-currency"
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#8B3FC5]"
              >
                {['QAR', 'USD', 'EUR', 'AED', 'SAR', 'GBP', 'JPY'].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Sector</label>
              <input
                type="text"
                value={form.sector}
                onChange={(e) => setForm({ ...form, sector: e.target.value })}
                placeholder="SaaS / Retail / …"
                data-testid="finance-create-sector"
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#8B3FC5]"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              data-testid="finance-create-notes"
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#8B3FC5]"
            />
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="finance-create-submit"
            className="px-4 py-2 bg-[#0F1A2D] text-white rounded-lg text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? 'Saving…' : <><Save className="w-4 h-4" /> Create</>}
          </button>
        </div>
      </div>
    </div>
  );
}
